import React from "react";
import { useI18n } from "../../i18n";
import CompleteSaleButton from "./CompleteSaleButton";
import CustomerScan from "./CustomerScan";
import DiscountRow from "./DiscountRow";
import PaymentSection from "./PaymentSection";
import TicketActions from "./TicketActions";
import TicketLines from "./TicketLines";
import TicketTotals from "./TicketTotals";
import { hasTab, paymentState } from "../utils/payment";

/**
 * The always-visible ticket column used from tablet width up.
 *
 * The header and the complete button are pinned; everything between them
 * scrolls as one, so a long ticket or an open keypad can never push the
 * payment controls out of reach.
 */
const TicketPanel = ({
  ticket,
  currency,
  payment,
  customer,
  onCustomerPicked,
  onCustomerCleared,
  canExtendCredit,
  onHold,
  onClear,
}) => {
  const { t } = useI18n();
  const { isShort, isCreditBlocked } = paymentState(
    payment.method,
    payment.tendered,
    ticket.totals.total,
    hasTab(customer, payment.creditTab),
  );

  return (
    <aside className="hidden lg:flex flex-col w-[380px] xl:w-[420px] shrink-0 h-[calc(100vh-3.5rem)] sticky top-14 bg-slate-900 border-s border-slate-800">
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div>
          <h2 className="text-base font-bold text-white">
            {t("pos.ticket.title")}
          </h2>
          <p className="text-xs text-slate-400">
            {t("common.items", { count: ticket.totals.itemCount })}
          </p>
        </div>
        <TicketActions
          disabled={ticket.isEmpty}
          onHold={onHold}
          onClear={onClear}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <TicketLines
          lines={ticket.lines}
          currency={currency}
          onSetQuantity={ticket.setQuantity}
          onRemove={ticket.removeProduct}
        />

        {/* The card belongs to this basket, so it is offered once there is one */}
        {!ticket.isEmpty && (
          <CustomerScan
            customer={customer}
            onPicked={onCustomerPicked}
            onCleared={onCustomerCleared}
            currency={currency}
          />
        )}

        {!ticket.isEmpty && (
          <DiscountRow
            discount={ticket.discount}
            discountAmount={ticket.totals.discountAmount}
            currency={currency}
            onChange={ticket.setDiscount}
          />
        )}

        <TicketTotals totals={ticket.totals} currency={currency} compact />

        <PaymentSection
          totals={ticket.totals}
          currency={currency}
          disabled={ticket.isEmpty}
          compact
          showCompleteButton={false}
          customer={customer}
          canExtendCredit={canExtendCredit}
          {...payment}
        />
      </div>

      <div className="shrink-0 p-3 border-t border-slate-800 bg-slate-900">
        <CompleteSaleButton
          total={ticket.totals.total}
          currency={currency}
          submitting={payment.submitting}
          disabled={
            ticket.isEmpty || payment.submitting || isShort || isCreditBlocked
          }
          onClick={payment.onComplete}
        />
      </div>
    </aside>
  );
};

export default TicketPanel;
